<script lang="ts">
	import { createEventDispatcher } from 'svelte';

	import { format } from 'src/services/i18n';

	import type { MenuListDataType, ProfileMenuEvents } from './DropdownItem.types';
	import { profiles } from 'src/stores/features/profiles';
	import {
		CircleAvatar,
		DropdownMenu,
		DropdownMenuItem,
		IconButton,
		MenuItemList,
		Text,
	} from '@Shelly/shelly-design-system/index';
	import { currentUnitId, homes } from 'src/stores/account/home';
	import HomeRow from '../Homes/HomeRow.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import HomeList from './HomeList.svelte';
	import CrazyDivider from './CrazyDivider.svelte';
	import { user_data } from 'src/stores/account/user';
	import { findOwnedHome } from 'src/helpers/houses/homes_helpers';

	export let showHomeList = false;

	const dispatch = createEventDispatcher<ProfileMenuEvents>();

	let showActiveHomeSubmenu = false;

	$: selectedProfile = $profiles.find((profile) => profile.isSelected);
	$: isInstaller = $user_data.installer;
	$: selectedHome = $homes.list.find((home) => home.id === $currentUnitId);
	$: displayHome = isInstaller
		? findOwnedHome(
				$homes.list,
				String($user_data.user_id),
				$user_data?.available_units ?? {},
				$currentUnitId,
			)
		: selectedHome;

	const handleMenuClick = (e: MouseEvent) => {
		e.stopPropagation();
		showActiveHomeSubmenu = !showActiveHomeSubmenu;
	};

	const handleToggleHomeMenu = () => {
		showHomeList = !showHomeList;
	};

	const handleAddProfile = () => {
		showActiveHomeSubmenu = false;
		dispatch('addProfile');
	};

	const handleProfileClick = (profile: MenuListDataType) => {
		showActiveHomeSubmenu = false;
		dispatch('profileClick', profile);
	};
</script>

<div class="homes-container">
	<div class="selected-profile-trigger width-full gap-s">
		<CircleAvatar
			class="selected-profile-avatar"
			size="m"
			image={selectedProfile.acc_avatar?.image}
			text={selectedProfile.acc_avatar?.text}
			icon={selectedProfile.acc_avatar?.icon}
			backgroundColor={selectedProfile.acc_avatar?.backgroundColor}
			avatarOptions={selectedProfile.acc_avatar?.avatarOptions}
		/>
		<div class="selected-profile-text">
			<Text noWrap styleType="body">{selectedProfile.title}</Text>
			<Text noWrap styleType="tiny" class="text-color-3">{selectedProfile.email}</Text>
		</div>
		<DropdownMenu
			bind:show={showActiveHomeSubmenu}
			on:close={() => {
				showActiveHomeSubmenu = false;
			}}
			width={null}
			maxWidth={280}
			hideStaticBottom={$profiles.length <= 1}
		>
			<IconButton
				slot="trigger"
				class="profile-switch-btn"
				isActive={showActiveHomeSubmenu}
				icon="fa-regular fa-arrow-left-arrow-right"
				variant="secondary"
				aria-label={$format('dropdown_profile_change_profile', {
					default: 'Change profile',
				})}
				on:click={handleMenuClick}
			/>
			{#if $profiles.length > 1}
				<Text class="pt-s pl-m pb-s"
					>{$format('dropdown_profile_change_profile', { default: 'Change profile' })}</Text
				>
			{/if}
			{#each $profiles as profile}
				{#if !profile.isSelected}
					<DropdownMenuItem
						slot="trigger"
						isGroupItem
						title={profile.title}
						subtitle={profile.email}
						avatar={profile.acc_avatar}
						selected={false}
						rightIconName="fa-regular fa-arrow-left-arrow-right"
						rightIconStyle="color: var(--color-white, #ffffff); "
						rightIcon={false}
						on:click={() => handleProfileClick(profile)}
					/>
				{/if}
			{/each}
			{#if $profiles.length <= 1}
				<MenuItemList
					isGroupItem
					class={'pl-m-plus pr-m-plus'}
					title={capFirstChar(
						$format('button_profile_add', {
							default: 'Add another profile',
						}),
					)}
					icon={'fa-solid fa-user-plus'}
					on:click={handleAddProfile}
				/>
			{/if}
			<svelte:fragment slot="staticBottom">
				{#if $profiles.length > 1}
					<MenuItemList
						isGroupItem
						class={'pl-m-plus pr-m-plus'}
						title={capFirstChar(
							$format('button_profile_add', {
								default: 'Add another profile',
							}),
						)}
						icon={'fa-solid fa-user-plus'}
						on:click={handleAddProfile}
					/>
					<MenuItemList
						isGroupItem
						class={'pl-m-plus pr-m-plus'}
						title={$format('log_out_all_profiles_title', {
							default: 'Log out from all profiles',
						})}
						icon={'fa-solid fa-right-from-bracket'}
						on:click={() => dispatch('logoutAllProfiles')}
					/>
				{/if}
			</svelte:fragment>
		</DropdownMenu>
	</div>
	<CrazyDivider />
	<HomeRow
		home={displayHome}
		isActive={true}
		isHomeListOpen={showHomeList}
		on:toggleHomeMenu={handleToggleHomeMenu}
		on:select={(e) => dispatch('homeClick', e.detail)}
	/>

	{#if showHomeList && !isInstaller}
		<HomeList on:selectHome={(e) => dispatch('homeClick', e.detail)} />
	{/if}
</div>

<style>
	.homes-container {
		margin: var(--spacing-s);
		background-color: var(--color-1);
		border-radius: var(--radius-big);
		max-height: 550px;
		overflow-y: auto;
	}

	.selected-profile-trigger {
		display: flex;
		align-items: center;
		padding: var(--spacing-s-plus);
		box-sizing: border-box;
	}

	.selected-profile-avatar {
		margin-right: var(--spacing-s-plus);
		flex-shrink: 0;
	}

	.selected-profile-text {
		display: flex;
		flex: 1;
		flex-direction: column;
		justify-content: center;
		overflow: hidden;
		min-width: 0;
	}

	.profile-switch-btn {
		flex-shrink: 0;
		margin-left: var(--spacing-s-plus);
	}

	.profile-switch-btn:global(.icon-button.secondary.active),
	.profile-switch-btn:global(.icon-button.secondary:not(.disabled-overlay):active) {
		background: var(--color-2);
		box-shadow: none;
	}
</style>
